import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LOGO, HERO } from "../lib/constants";
import { apiErr } from "../lib/api";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/feed";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/feed", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password); toast.success("Welcome back 💛"); navigate("/feed"); }
    catch (err) { toast.error(apiErr(err)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-bronze/60 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-serif text-3xl leading-tight">"Have a penny, leave a penny.<br/>Need a penny, take a penny."</p>
          <p className="mt-3 text-white/80">A dignity-first place to ask for help — and to give it.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-10 sm:p-12 bg-background paper-bg">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img src={LOGO} alt="HAPLAP" className="w-12 h-12 rounded-xl" />
            <span className="font-serif font-bold text-2xl text-bronze">HAPLAP</span>
          </Link>
          <h1 className="font-serif text-3xl font-bold text-bronze">Welcome back</h1>
          <p className="text-muted-foreground mt-1 mb-6">Sign in to your community.</p>

          <Button data-testid="google-login-btn" onClick={googleLogin} variant="outline"
            className="w-full rounded-full py-6 border-border">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5 mr-2" />
            Continue with Google
          </Button>
          <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" /> OR <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-xl mt-1" />
            </div>
            <div>
              <Label>Password</Label>
              <div className="relative mt-1">
                <Input data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-xl pr-11" />
                <button type="button" data-testid="toggle-login-password" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-copper transition-colors">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full bg-copper hover:bg-copper/90 text-white rounded-full py-6">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-center mt-5 text-muted-foreground">
            New here? <Link to="/register" className="text-copper font-semibold" data-testid="to-register">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
